import React from 'react';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { type ProfitLossStatement } from '../../services/api';

interface ProfitLossReportProps {
  data?: ProfitLossStatement;
  isLoading: boolean;
  dateRange: { startDate: string; endDate: string };
  showComparison: boolean;
}

const ProfitLossReport: React.FC<ProfitLossReportProps> = ({ 
  data, 
  isLoading, 
  dateRange, 
  showComparison 
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected period</p>
      </div>
    );
  }

  const calculateChange = (current: number, previous?: number) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Profit & Loss Statement</h3>
          <p className="text-sm text-gray-500">
            {new Date(data.period.startDate).toLocaleDateString()} - {new Date(data.period.endDate).toLocaleDateString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Net Income</p>
          <p className={`text-2xl font-bold ${data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(data.netIncome)}
          </p>
          {showComparison && data.comparison && (
            <div className={`flex items-center gap-1 text-sm ${
              calculateChange(data.netIncome, data.comparison.netIncome) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {calculateChange(data.netIncome, data.comparison.netIncome) >= 0 ? 
                <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />
              }
              <span>{formatPercentage(calculateChange(data.netIncome, data.comparison.netIncome))}</span>
            </div>
          )}
        </div>
      </div>

      {/* Revenue Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Revenue</h4>
        <div className="space-y-2">
          {data.revenue.items.map((item, index) => (
            <div key={index} className="flex justify-between items-center">
              <div>
                <span className="text-sm font-medium">{item.name}</span>
                <span className="text-xs text-gray-500 ml-2">{item.code}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                {showComparison && data.comparison && (
                  <div className="text-xs text-gray-500">
                    Prev: {formatCurrency(data.comparison.revenue * (item.amount / data.revenue.total))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between items-center font-semibold">
              <span>Total Revenue</span>
              <span>{formatCurrency(data.revenue.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cost of Goods Sold */}
      <div className="bg-red-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Cost of Goods Sold</h4>
        <div className="space-y-2">
          {data.costOfGoodsSold.items.map((item, index) => (
            <div key={index} className="flex justify-between items-center">
              <div>
                <span className="text-sm font-medium">{item.name}</span>
                <span className="text-xs text-gray-500 ml-2">{item.code}</span>
              </div>
              <span className="text-sm font-medium text-red-600">
                ({formatCurrency(item.amount)})
              </span>
            </div>
          ))}
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between items-center font-semibold text-red-600">
              <span>Total COGS</span>
              <span>({formatCurrency(data.costOfGoodsSold.total)})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gross Profit */}
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-900">Gross Profit</span>
          <div className="text-right">
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(data.grossProfit)}
            </span>
            <div className="text-sm text-gray-600">
              Margin: {formatPercentage(data.grossProfitMargin)}
            </div>
          </div>
        </div>
      </div>

      {/* Operating Expenses */}
      <div className="bg-orange-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Operating Expenses</h4>
        {Object.entries(data.operatingExpenses.categories).map(([category, data], categoryIndex) => (
          <div key={categoryIndex} className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2 capitalize">
              {category.replace('_', ' ')}
            </h5>
            <div className="space-y-1 ml-4">
              {data.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <span className="text-sm">{item.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                  </div>
                  <span className="text-sm text-orange-600">
                    ({formatCurrency(item.amount)})
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-center font-medium text-orange-600 pt-1 border-t">
                <span>Total {category.replace('_', ' ')}</span>
                <span>({formatCurrency(data.total)})</span>
              </div>
            </div>
          </div>
        ))}
        <div className="border-t pt-2 mt-2">
          <div className="flex justify-between items-center font-semibold text-orange-600">
            <span>Total Operating Expenses</span>
            <span>({formatCurrency(data.operatingExpenses.total)})</span>
          </div>
        </div>
      </div>

      {/* Other Expenses */}
      {data.otherExpenses.items.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Other Expenses</h4>
          <div className="space-y-2">
            {data.otherExpenses.items.map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <div>
                  <span className="text-sm font-medium">{item.name}</span>
                  <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                </div>
                <span className="text-sm font-medium text-red-600">
                  ({formatCurrency(item.amount)})
                </span>
              </div>
            ))}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between items-center font-semibold text-red-600">
                <span>Total Other Expenses</span>
                <span>({formatCurrency(data.otherExpenses.total)})</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Net Income */}
      <div className={`p-4 rounded-lg ${data.netIncome >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-900">Net Income</span>
          <div className="text-right">
            <span className={`text-xl font-bold ${data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(data.netIncome)}
            </span>
            <div className="text-sm text-gray-600">
              Margin: {formatPercentage(data.netProfitMargin)}
            </div>
            {showComparison && data.comparison && (
              <div className={`flex items-center gap-1 text-sm ${
                calculateChange(data.netIncome, data.comparison.netIncome) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {calculateChange(data.netIncome, data.comparison.netIncome) >= 0 ? 
                  <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />
                }
                <span>{formatPercentage(calculateChange(data.netIncome, data.comparison.netIncome))}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossReport;
